import { useState, useRef } from 'react';
import { Smartphone, Key, Shield, Check, Loader2, Copy, CheckCircle, Lock, FolderOpen, Upload } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import type { VaultTenantInput } from '../lib/auth-context';

interface QuickConnectProps {
  onConnect: (url: string, apiKey: string, remember: boolean) => Promise<void>;
  onImportVault?: (
    entries: VaultTenantInput[]
  ) => Promise<{ ok: number; failed: Array<{ label: string; error: string }> }>;
  onOpenSaved?: () => Promise<void>;
  onClearVault?: () => void;
  hasSavedVault?: boolean;
}

function parseVaultFile(raw: unknown): VaultTenantInput[] {
  if (!raw || typeof raw !== 'object') throw new Error('Invalid vault JSON');
  const obj = raw as Record<string, unknown>;

  // ~/.shadowguard/tenants.json shape
  if (Array.isArray(obj.tenants)) {
    return (obj.tenants as Array<Record<string, unknown>>).map((t) => {
      if (!t.apiKey || typeof t.apiKey !== 'string') throw new Error('Each tenant needs apiKey');
      return {
        tenantId: String(t.tenantId || t.id || ''),
        label: typeof t.label === 'string' ? t.label : undefined,
        domain: typeof t.domain === 'string' ? t.domain : undefined,
        apiKey: t.apiKey,
        controlPlaneUrl:
          typeof t.controlPlaneUrl === 'string'
            ? t.controlPlaneUrl
            : typeof obj.controlPlaneUrl === 'string'
              ? obj.controlPlaneUrl
              : undefined,
        agentId: typeof t.agentId === 'string' ? t.agentId : undefined,
      };
    });
  }

  // Flat array of { tenantId, apiKey, ... }
  if (Array.isArray(raw)) {
    return (raw as Array<Record<string, unknown>>).map((t) => ({
      tenantId: String(t.tenantId || t.id || ''),
      label: typeof t.label === 'string' ? t.label : undefined,
      domain: typeof t.domain === 'string' ? t.domain : undefined,
      apiKey: String(t.apiKey),
      controlPlaneUrl: typeof t.controlPlaneUrl === 'string' ? t.controlPlaneUrl : undefined,
    }));
  }

  throw new Error('Unrecognized vault format — use ~/.shadowguard/tenants.json');
}

export function QuickConnect({
  onConnect,
  onImportVault,
  onOpenSaved,
  onClearVault,
  hasSavedVault,
}: QuickConnectProps) {
  const [step, setStep] = useState<'method' | 'manual' | 'qr' | 'pin' | 'vault'>('method');
  const [url, setUrl] = useState('https://shadowguard-control-plane-v2.simeon-garratt.workers.dev');
  const [apiKey, setApiKey] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [manualHint, setManualHint] = useState<string | null>(null);
  const [vaultStatus, setVaultStatus] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleConnect = async () => {
    if (!url || !apiKey) return;
    setConnecting(true);
    setError(null);
    try {
      await onConnect(url, apiKey, rememberDevice);
      setManualHint(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Connection failed';
      // Check if it's a rate limit error
      if (message.includes('429') || message.toLowerCase().includes('too many')) {
        setError('Too many login attempts. Please wait 1 minute and try again.');
      } else {
        setError(message);
      }
    } finally {
      setConnecting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const quickConnectUrl = `shadowguard://connect?url=${encodeURIComponent(url)}&key=${encodeURIComponent(apiKey)}`;

  if (step === 'method') {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center p-4 sm:p-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="w-full max-w-md">
          <div className="text-center mb-6 sm:mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-blue-500/10 rounded-2xl mb-4">
              <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-blue-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Welcome to ShadowGuard</h1>
            <p className="text-gray-400 text-sm sm:text-base">Choose how you'd like to connect</p>
          </div>

          <div className="space-y-3">
            {hasSavedVault && onOpenSaved && (
              <button
                onClick={async () => {
                  setConnecting(true);
                  setError(null);
                  try {
                    await onOpenSaved();
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to open saved projects');
                  } finally {
                    setConnecting(false);
                  }
                }}
                disabled={connecting}
                className="w-full p-6 bg-blue-500/10 hover:bg-blue-500/15 border border-blue-500/40 hover:border-blue-400 rounded-xl transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <FolderOpen className="w-6 h-6 text-blue-300" />
                  </div>
                  <div className="text-left flex-1">
                    <div className="text-white font-semibold mb-1">Open saved projects</div>
                    <div className="text-sm text-gray-400">
                      Reconnect to all projects stored in this browser
                    </div>
                  </div>
                  {connecting ? (
                    <Loader2 className="w-5 h-5 text-blue-300 animate-spin" />
                  ) : (
                    <div className="text-blue-300">→</div>
                  )}
                </div>
              </button>
            )}

            {onImportVault && (
              <button
                onClick={() => setStep('vault')}
                className="w-full p-6 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.1] hover:border-emerald-500/50 rounded-xl transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                    <Upload className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div className="text-left flex-1">
                    <div className="text-white font-semibold mb-1">Import local vault</div>
                    <div className="text-sm text-gray-400">
                      Load ~/.shadowguard/tenants.json — all projects at once
                    </div>
                  </div>
                  <div className="text-gray-400 group-hover:text-emerald-400 transition-colors">→</div>
                </div>
              </button>
            )}

            <button
              onClick={() => setStep('manual')}
              className="w-full p-6 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.1] hover:border-blue-500/50 rounded-xl transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                  <Key className="w-6 h-6 text-blue-400" />
                </div>
                <div className="text-left flex-1">
                  <div className="text-white font-semibold mb-1">Single API key</div>
                  <div className="text-sm text-gray-400">Connect one project only</div>
                </div>
                <div className="text-gray-400 group-hover:text-blue-400 transition-colors">→</div>
              </div>
            </button>

            <button
              onClick={() => setStep('pin')}
              className="w-full p-6 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.1] hover:border-green-500/50 rounded-xl transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center group-hover:bg-green-500/20 transition-colors">
                  <Lock className="w-6 h-6 text-green-400" />
                </div>
                <div className="text-left flex-1">
                  <div className="text-white font-semibold mb-1">6-Digit PIN</div>
                  <div className="text-sm text-gray-400">Simple, secure passcode (Recommended)</div>
                </div>
                <div className="text-gray-400 group-hover:text-green-400 transition-colors">→</div>
              </div>
            </button>

            <button
              onClick={() => setStep('qr')}
              className="w-full p-6 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.1] hover:border-blue-500/50 rounded-xl transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                  <Smartphone className="w-6 h-6 text-purple-400" />
                </div>
                <div className="text-left flex-1">
                  <div className="text-white font-semibold mb-1">Quick Connect</div>
                  <div className="text-sm text-gray-400">Use a connection link or QR code</div>
                </div>
                <div className="text-gray-400 group-hover:text-purple-400 transition-colors">→</div>
              </div>
            </button>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="mt-6 p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg">
            <div className="flex gap-3">
              <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-300">
                <span className="font-semibold text-white">Local multi-project:</span> import{' '}
                <code className="text-xs text-blue-300">~/.shadowguard/tenants.json</code>, then switch
                projects in the sidebar. Sign out keeps the vault; use Clear vault to wipe this browser.
              </div>
            </div>
          </div>

          {hasSavedVault && onClearVault && (
            <button
              type="button"
              onClick={() => {
                if (confirm('Remove all saved project keys from this browser?')) onClearVault();
              }}
              className="mt-4 w-full text-xs text-gray-500 hover:text-red-400 transition-colors"
            >
              Clear saved vault on this browser
            </button>
          )}
        </div>
      </div>
    );
  }

  if (step === 'vault' && onImportVault) {
    const handleFile = async (file: File) => {
      setConnecting(true);
      setError(null);
      setVaultStatus(null);
      try {
        const text = await file.text();
        const parsed = parseVaultFile(JSON.parse(text));
        if (parsed.length === 0) throw new Error('Vault file has no tenants');
        setVaultStatus(`Importing ${parsed.length} project(s)…`);
        const result = await onImportVault(parsed);
        if (result.ok === 0) {
          throw new Error(
            result.failed.map((f) => `${f.label}: ${f.error}`).join('; ') || 'Import failed'
          );
        }
        setVaultStatus(
          `Loaded ${result.ok} project(s)` +
            (result.failed.length ? ` (${result.failed.length} failed)` : '')
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Import failed');
        setVaultStatus(null);
      } finally {
        setConnecting(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="w-full max-w-md">
          <button
            onClick={() => {
              setStep('method');
              setError(null);
              setVaultStatus(null);
            }}
            className="text-gray-400 hover:text-white mb-6 flex items-center gap-2"
          >
            ← Back
          </button>

          <div className="bg-white/[0.03] border border-white/[0.1] rounded-xl p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/10 rounded-2xl mb-4">
                <Upload className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Import local vault</h2>
              <p className="text-gray-400 text-sm">
                Choose <code className="text-emerald-300/90">tenants.json</code> from{' '}
                <code className="text-emerald-300/90">~/.shadowguard/</code> on this machine.
              </p>
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleFile(f);
              }}
            />

            <button
              type="button"
              disabled={connecting}
              onClick={() => fileRef.current?.click()}
              className="w-full py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {connecting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Importing…
                </>
              ) : (
                <>
                  <FolderOpen className="w-5 h-5" />
                  Choose tenants.json
                </>
              )}
            </button>

            {vaultStatus && (
              <p className="mt-4 text-sm text-center text-emerald-300">{vaultStatus}</p>
            )}
            {error && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            <p className="mt-6 text-xs text-gray-500 text-center leading-relaxed">
              Keys stay in this browser’s local storage so you can switch projects without re-pasting.
              They never leave your machine except to log into your control plane.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'pin') {
    const [pin, setPin] = useState(['', '', '', '', '', '']);
    const [pinError, setPinError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const inputRefs = useState<(HTMLInputElement | null)[]>([])[0];

    const handlePinChange = (index: number, value: string) => {
      if (value && !/^\d$/.test(value)) return;
      const newPin = [...pin];
      newPin[index] = value;
      setPin(newPin);
      setPinError('');

      if (value && index < 5) {
        inputRefs[index + 1]?.focus();
      }

      // Auto-submit when complete
      if (value && index === 5 && newPin.every(d => d !== '')) {
        verifyPin(newPin.join(''));
      }
    };

    const handlePinKeyDown = (index: number, e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && !pin[index] && index > 0) {
        inputRefs[index - 1]?.focus();
      }
    };

    const verifyPin = async (pinCode: string) => {
      setVerifying(true);
      setPinError('');
      
      try {
        // Hash PIN
        const encoder = new TextEncoder();
        const data = encoder.encode(pinCode);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

        const storedHash = localStorage.getItem('sg_pin_hash');
        const pinCreated = localStorage.getItem('sg_pin_created');

        if (!storedHash) {
          // First-time setup.
          localStorage.setItem('sg_pin_hash', hashHex);
          localStorage.setItem('sg_pin_created', new Date().toISOString());
          setManualHint('PIN set. Enter your API key to continue.');
          setStep('manual');
          setVerifying(false);
          return;
        }

        // Check PIN expiration (30 days)
        if (pinCreated) {
          const created = new Date(pinCreated);
          const now = new Date();
          const daysSinceCreation = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
          
          if (daysSinceCreation > 30) {
            // PIN expired - clear and require setup
            localStorage.removeItem('sg_pin_hash');
            localStorage.removeItem('sg_pin_created');
            setPinError('PIN expired (30 days). Please set a new PIN.');
            setPin(['', '', '', '', '', '']);
            inputRefs[0]?.focus();
            setVerifying(false);
            return;
          }
        }

        // Verify PIN
        if (hashHex === storedHash) {
          setManualHint('PIN verified. Enter your API key to continue.');
          setStep('manual');
        } else {
          setPinError('Incorrect PIN');
          setPin(['', '', '', '', '', '']);
          inputRefs[0]?.focus();
        }
      } catch (err) {
        setPinError('Authentication failed');
      } finally {
        setVerifying(false);
      }
    };

    const isSetup = !localStorage.getItem('sg_pin_hash');

    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="w-full max-w-md">
          <button
            onClick={() => setStep('method')}
            className="text-gray-400 hover:text-white mb-6 flex items-center gap-2"
          >
            ← Back
          </button>

          <div className="bg-white/[0.03] border border-white/[0.1] rounded-xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/10 rounded-2xl mb-4">
                <Lock className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {isSetup ? 'Set Your PIN' : 'Enter Your PIN'}
              </h2>
              <p className="text-gray-400 text-sm">
                {isSetup 
                  ? 'Create a 6-digit PIN to secure your dashboard' 
                  : 'Enter your 6-digit PIN to continue'
                }
              </p>
            </div>

            <div className="flex justify-center gap-2 mb-6">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(index, e)}
                  disabled={verifying}
                  className={`w-12 h-14 text-center text-2xl font-bold bg-gray-900 border ${
                    pinError ? 'border-red-500' : 'border-white/[0.1] focus:border-green-500'
                  } rounded-lg text-white focus:outline-none transition-colors disabled:opacity-50`}
                />
              ))}
            </div>

            {pinError && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg mb-4">
                <p className="text-red-400 text-sm text-center">{pinError}</p>
              </div>
            )}

            {isSetup && (
              <div className="p-4 bg-green-500/5 border border-green-500/20 rounded-lg mb-4">
                <div className="flex gap-3">
                  <Shield className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-300">
                    <span className="font-semibold text-white">First time setup.</span> PIN protects local access flow, but API keys are no longer stored on this device.
                  </div>
                </div>
              </div>
            )}

            {verifying && (
              <div className="text-center py-2">
                <Loader2 className="w-6 h-6 text-green-400 animate-spin mx-auto" />
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => setStep('manual')}
              className="text-sm text-gray-400 hover:text-gray-300"
            >
              Use API Key instead →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'qr') {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="w-full max-w-md">
          <button
            onClick={() => setStep('method')}
            className="text-gray-400 hover:text-white mb-6 flex items-center gap-2"
          >
            ← Back
          </button>

          <div className="bg-white/[0.03] border border-white/[0.1] rounded-xl p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Quick Connect</h2>
              <p className="text-gray-400 text-sm">Scan this QR code or copy the connection link</p>
            </div>

            <div className="bg-white p-8 rounded-lg mb-6 flex items-center justify-center">
              <QRCodeSVG 
                value={quickConnectUrl}
                size={256}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Connection Link</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={quickConnectUrl}
                    readOnly
                    className="flex-1 bg-gray-900 border border-white/[0.1] rounded-lg px-4 py-2 text-white text-sm font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(quickConnectUrl)}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button
                onClick={() => setStep('manual')}
                className="w-full px-4 py-3 bg-white/[0.06] hover:bg-white/[0.08] text-white rounded-lg transition-colors"
              >
                Enter API Key Manually
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="w-full max-w-md">
        <button
          onClick={() => setStep('method')}
          className="text-gray-400 hover:text-white mb-6 flex items-center gap-2"
        >
          ← Back
        </button>

        <div className="bg-white/[0.03] border border-white/[0.1] rounded-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500/10 rounded-2xl mb-4">
              <Key className="w-8 h-8 text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Connect to ShadowGuard</h2>
            <p className="text-gray-400 text-sm">Enter your API credentials to get started</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Control Plane URL</label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://your-control-plane.workers.dev"
                className="w-full bg-gray-900 border border-white/[0.1] focus:border-blue-500 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">API Key</label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleConnect()}
                placeholder="sg_..."
                className="w-full bg-gray-900 border border-white/[0.1] focus:border-blue-500 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none transition-colors"
              />
            </div>

            <label className="flex items-center gap-3 p-4 bg-white/[0.02] border border-white/[0.05] rounded-lg cursor-pointer hover:bg-white/[0.04] transition-colors">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
              />
              <div className="flex-1">
                <div className="text-sm font-medium text-white">Trust this device</div>
                <div className="text-xs text-gray-400 mt-0.5">Stay logged in on this device</div>
              </div>
              <Check className={`w-5 h-5 transition-opacity ${rememberDevice ? 'text-green-400 opacity-100' : 'opacity-0'}`} />
            </label>

            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
            {manualHint && (
              <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                <p className="text-green-300 text-sm">{manualHint}</p>
              </div>
            )}

            <button
              onClick={handleConnect}
              disabled={connecting || !url || !apiKey}
              className="w-full py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
            >
              {connecting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  Connect to Dashboard
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-400">
            Don't have an API key?{' '}
            <a href="https://docs.shadowguard.io/getting-started" className="text-blue-400 hover:text-blue-300">
              View setup guide
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
